import {
  mdiCheckCircle,
  mdiChevronDown,
  mdiChevronUp,
  mdiCircleOutline,
  mdiClockOutline,
  mdiCloseCircle
} from '@mdi/js';
import Icon from '@mdi/react';
import {
  Box,
  Collapse,
  Grid,
  IconButton,
  Step,
  StepConnector,
  stepConnectorClasses,
  StepIconProps,
  StepLabel,
  Stepper,
  styled,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { TipoAditivo } from '@vabembarqueja/embarque-ja-shared-components';
import { useMemo, useState } from 'react';

import { useUserContext } from '../../../contexts/User.context';
import { FaseSolicitacao, SolicitacaoState } from '../../../models/Usuario';
import { UsuarioService } from '../../../services';
import { Colors } from '../../../shared/colors';
import { ModalAnexoAditivo } from '../extrato-usuario/ModalAnexoAditivo';

import { ExibirConsumoPosPago } from './ExibirConsumoPosPago';
import { ModalAnexoMinuta } from './ModalAnexoMinuta';

interface Props {
  dataParaNovoPedido?: Date | null;
  posPagoAtivo: boolean;
  fases?: Array<FaseSolicitacao>;
  statusSolicitacao: SolicitacaoState;
  orgaoPublico: boolean;
  statusAditivo: TipoAditivo | null;
}

const QontoConnector = styled(StepConnector, {
  shouldForwardProp: (prop) => prop !== 'isRejected'
})<{ isRejected?: boolean; previousRejected?: boolean }>(
  ({ theme, isRejected, previousRejected }) => ({
    [`&.${stepConnectorClasses.alternativeLabel}`]: {
      top: 10,
      left: 'calc(-50% + 16px)',
      right: 'calc(50% + 16px)'
    },
    [`&.${stepConnectorClasses.active}`]: {
      [`& .${stepConnectorClasses.line}`]: {
        borderColor: previousRejected
          ? Colors.gray3
          : isRejected
          ? Colors.error.dark
          : Colors.primary.main
      }
    },
    [`&.${stepConnectorClasses.completed}`]: {
      [`& .${stepConnectorClasses.line}`]: {
        borderColor: Colors.primary.main
      }
    },
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : '#eaeaf0',
      borderTopWidth: 3,
      borderRadius: 1
    }
  })
);

const QontoStepIconRoot = styled('div')<{ ownerState: { active?: boolean } }>(
  ({ theme, ownerState }) => ({
    color: theme.palette.mode === 'dark' ? theme.palette.grey[700] : '#eaeaf0',
    display: 'flex',
    width: 22,
    height: 22,
    flexShrink: 0,
    alignItems: 'center',
    ...(ownerState.active && {
      color: Colors.primary.main
    }),
    '& .QontoStepIcon-completedIcon': {
      color: Colors.primary.main,
      zIndex: 1,
      fontSize: 18
    },
    '& .QontoStepIcon-circle': {
      width: 8,
      height: 8,
      borderRadius: '50%',
      backgroundColor: 'currentColor'
    }
  })
);

function QontoStepIcon(props: StepIconProps) {
  const { active, completed, className, error } = props;
  const iconPath = error
    ? mdiCloseCircle
    : completed
    ? mdiCheckCircle
    : active
    ? mdiClockOutline
    : mdiCircleOutline;
  const iconColor = error
    ? Colors.error.dark
    : completed || active
    ? Colors.primary.main
    : Colors.border.input;

  return (
    <QontoStepIconRoot ownerState={{ active }} className={className}>
      <Icon path={iconPath} color={iconColor} style={{ width: '100%', height: '100%' }} />
    </QontoStepIconRoot>
  );
}

const ConsumoPosPago = ({
  dataParaNovoPedido,
  fases,
  statusSolicitacao,
  posPagoAtivo,
  statusAditivo,
  orgaoPublico
}: Props) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { refreshUserInfo, posPagoState } = useUserContext();
  const [isAditivoMinimized, setIsAditivoMinimized] = useState(false);

  const aditivoReprovado = statusAditivo === TipoAditivo.Reprovado;
  const posPagoRejeitado = statusSolicitacao === SolicitacaoState.REPROVADO;
  const posPagoNaoSolicitado = statusSolicitacao === SolicitacaoState.NAO_SOLICITADO;

  const isAditivoAprovado = useMemo(() => {
    if (!fases) return false;
    const fasesAditivosAtuais = fases.filter((f) => f.tipoProcesso === 1);

    if (fasesAditivosAtuais.length === 0) return false;

    // As fases não completas vêm da API com dataResposta === null
    const hasPendingPhase = fasesAditivosAtuais.some((f) => f.dataResposta === null);

    // Adicionamos a checagem da última fase (Aditivo Aprovado) que inserimos mockada no step anterior caso
    // o processamento tenha ocorrido ou olhamos diretamente pro status do aditivo dependendo da modelagem.
    // Como a lógica do processedFases insere o "Aditivo Aprovado" localmente, a checagem anterior dependia disso.
    // Para simplificar no topo, se não há fase pendente e existe a fase 'Coleta de Assinaturas' concluída:
    const coletaConcluida = fasesAditivosAtuais.some(
      (f) => f.nomeFase === 'Coleta de Assinaturas' && f.dataResposta !== null
    );

    return coletaConcluida && !hasPendingPhase;
  }, [fases]);

  const title = useMemo(() => {
    if (posPagoRejeitado || aditivoReprovado) return 'Solicitação rejeitada';
    if (isAditivoAprovado) return 'Solicitação de aditivo concluída!';
    return 'Solicitação em análise!';
  }, [posPagoRejeitado, aditivoReprovado, isAditivoAprovado]);

  const showModalAnexoMinutaCondition = useMemo(() => {
    if (posPagoNaoSolicitado) return true;

    if (posPagoRejeitado && dataParaNovoPedido) {
      const hoje = new Date();
      hoje.setHours(0, 0, 0, 0);
      const dataPermitida = new Date(dataParaNovoPedido);
      dataPermitida.setHours(0, 0, 0, 0);
      return hoje >= dataPermitida;
    }

    return false;
  }, [posPagoNaoSolicitado, posPagoRejeitado, dataParaNovoPedido]);

  const description = useMemo(() => {
    if (posPagoRejeitado) {
      return 'Sua solicitação para o plano pós-pago foi analisada, porém não conseguimos liberar essa modalidade para esse CNPJ no momento. Continue utilizando o Embarque Já efetuando cargas via Cartão (débito/crédito) ou Pix!';
    }

    if (aditivoReprovado) {
      return 'Seu pedido de aditivo contratual foi analisado, porém não foi aprovado no momento. Revise o documento e, se necessário, reenviar para nova avaliação.';
    }

    if (posPagoAtivo) {
      return 'Solicitação de migração para fazer parte do plano de consumo pós pago em análise. Assim que avaliarmos sua solicitação, informaremos o status dela por aqui.';
    }

    return 'Estamos analisando sua solicitação. Em breve, entraremos em contato com mais informações.';
  }, [posPagoRejeitado, aditivoReprovado, posPagoAtivo]);

  const descriptionAditivoFinal = 'Aditivo aprovado! O processo foi concluído com sucesso.';

  const processedFases = useMemo(() => {
    if (!fases) return [];

    const financeiroFase = fases.find((f) => f.nomeFase === 'Financeiro');
    const aditivoFase = fases.find((f) => f.nomeFase === 'Jurídico');

    if (financeiroFase && financeiroFase.dataCriacao && !posPagoAtivo) {
      const solicitacaoEnviadaFase = {
        nomeFase: 'Solicitação Enviada',
        ordemFase: 0,
        statusFase: null,
        dataCriacao: null,
        dataResposta: financeiroFase.dataCriacao,
        usuarioResposta: null,
        ressalva: false,
        faseAprovada: true,
        observacao: null,
        tipoProcesso: financeiroFase.tipoProcesso
      } as FaseSolicitacao;

      return [solicitacaoEnviadaFase, ...fases];
    }

    if (aditivoFase && aditivoFase.dataCriacao) {
      const solicitacaoEnviadaAditivoFase = {
        nomeFase: 'Solicitação Enviada para o Jurídico',
        ordemFase: 0,
        statusFase: null,
        dataCriacao: null,
        dataResposta: aditivoFase.dataCriacao,
        usuarioResposta: null,
        ressalva: false,
        faseAprovada: true,
        observacao: null,
        tipoProcesso: aditivoFase.tipoProcesso
      } as FaseSolicitacao;

      const fasesOrdenadas = [...fases].sort((a, b) => a.ordemFase - b.ordemFase);
      const ultimaFase = fasesOrdenadas[fasesOrdenadas.length - 1];

      const deveAdicionarAprovado =
        ultimaFase && ultimaFase.dataResposta && ultimaFase.faseAprovada;

      return deveAdicionarAprovado
        ? [
            solicitacaoEnviadaAditivoFase,
            ...fases,
            {
              nomeFase: 'Aditivo Aprovado',
              ordemFase: ultimaFase.ordemFase + 1,
              statusFase: 'Aprovada' as const,
              usuarioResposta: null,
              ressalva: false,
              faseAprovada: null,
              observacao: descriptionAditivoFinal,
              tipoProcesso: aditivoFase.tipoProcesso
            } as FaseSolicitacao
          ]
        : [solicitacaoEnviadaAditivoFase, ...fases];
    }

    return fases;
  }, [fases, posPagoAtivo, descriptionAditivoFinal]);

  const fasesContrato = useMemo(
    () => processedFases?.filter((f) => f.tipoProcesso === 0) ?? [],
    [processedFases]
  );
  const fasesAditivo = useMemo(
    () => processedFases?.filter((f) => f.tipoProcesso === 1) ?? [],
    [processedFases]
  );

  const descriptionAditivo = useMemo(() => {
    if (aditivoReprovado) {
      return 'Seu pedido de inclusão de aditivo contratual foi analisado, porém não foi aprovado no momento. Revise o documento e, se necessário, reenviar para nova avaliação.';
    }

    if (isAditivoAprovado) {
      return 'Seu aditivo já foi finalizado e enviado para o e-mail da conta. Sempre que precisar incluir um novo aditivo, basta clicar novamente no botão "Inserir Aditivo/Novo Contrato".';
    }

    return 'Solicitação de inclusão de aditivo contratual em análise. Após a validação do documento e das assinaturas, informaremos o status por aqui.';
  }, [aditivoReprovado, isAditivoAprovado]);

  const getActiveStep = (arr: FaseSolicitacao[]) => {
    if (!arr?.length) return 0;
    const sorted = [...arr].sort((a, b) => a.ordemFase - b.ordemFase);
    const firstIncompleteIndex = sorted.findIndex((f) => f.dataResposta === null);
    return firstIncompleteIndex === -1 ? sorted.length : firstIncompleteIndex;
  };

  const getStepStatus = (fase: FaseSolicitacao, completed: boolean, active: boolean) => {
    if (fase.dataResposta !== null && fase.faseAprovada === false) {
      return { label: 'Reprovado', color: Colors.error.dark };
    }
    if (completed) return { label: 'Concluído', color: Colors.primary.dark };
    if (active) return { label: 'Em análise', color: Colors.primary.dark };
    return { label: 'Não iniciado', color: Colors.gray1 };
  };

  const getMobileStepSx = (completed: boolean) =>
    isMobile
      ? {
          position: 'relative' as const,
          pb: 4,
          '&:last-child': { pb: 0 },
          '&:not(:last-child)::after': {
            content: '""',
            position: 'absolute' as const,
            left: 10,
            top: 11,
            bottom: -11,
            width: 2,
            bgcolor: completed ? Colors.primary.main : Colors.border.divider
          }
        }
      : {};

  const activeStepContrato = useMemo(() => getActiveStep(fasesContrato), [fasesContrato]);
  const activeStepAditivo = useMemo(() => getActiveStep(fasesAditivo), [fasesAditivo]);

  if (statusSolicitacao === SolicitacaoState.AVALIADO && posPagoAtivo === true) {
    return (
      <>
        {orgaoPublico && posPagoState?.posPagoAtivo && (
          <Box display="flex" justifyContent="flex-end" mb={2} width="100%">
            <ModalAnexoAditivo
              solicitarMigracao={async (file) => {
                try {
                  await UsuarioService.adicionarAditivo(file);
                  await refreshUserInfo();
                } catch (err) {
                  console.error('Erro ao solicitar migração:', err);
                }
              }}
              refreshUserInfo={refreshUserInfo}
              disabled={statusAditivo === TipoAditivo.EmAnalise}
            />
          </Box>
        )}
        {/* TIMELINE: ADITIVO */}
        {posPagoAtivo && fasesAditivo.some((fase) => fase.statusFase !== null) && (
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              mt: 4,
              mb: 4
            }}
          >
            <Box
              p={isMobile ? 2 : 5}
              display="flex"
              flexDirection="column"
              borderRadius={2}
              boxShadow="0px 5px 20px rgba(0, 0, 0, 0.15)"
            >
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" color={Colors.gray1}>
                  {title}
                </Typography>
                <IconButton onClick={() => setIsAditivoMinimized(!isAditivoMinimized)}>
                  <Icon path={isAditivoMinimized ? mdiChevronDown : mdiChevronUp} size={1} />
                </IconButton>
              </Box>

              <Collapse in={!isAditivoMinimized}>
                <Typography color={Colors.gray1} my={3} textAlign="justify">
                  {descriptionAditivo}
                </Typography>

                <Typography variant="subtitle2" color={Colors.gray1} mb={1}>
                  Fluxo de Aditivo
                </Typography>

                <Stepper
                  activeStep={activeStepAditivo}
                  orientation={isMobile ? 'vertical' : 'horizontal'}
                  sx={{ mt: 1 }}
                  connector={
                    isMobile ? null : <QontoConnector previousRejected={aditivoReprovado} />
                  }
                  alternativeLabel={!isMobile}
                >
                  {[...fasesAditivo]
                    .sort((a, b) => a.ordemFase - b.ordemFase)
                    .map((fase, index) => {
                      const { nomeFase, dataResposta, ordemFase, faseAprovada } = fase;

                      const isCompleted =
                        index < activeStepAditivo &&
                        (faseAprovada !== false || dataResposta !== null);
                      const stepStatus = getStepStatus(
                        fase,
                        isCompleted,
                        index === activeStepAditivo
                      );

                      return (
                        <Step
                          completed={isCompleted}
                          key={`a-${ordemFase}`}
                          sx={getMobileStepSx(isCompleted && faseAprovada !== false)}
                        >
                          <StepLabel
                            error={dataResposta !== null && faseAprovada === false}
                            StepIconComponent={QontoStepIcon}
                            sx={
                              isMobile
                                ? {
                                    alignItems: 'flex-start',
                                    py: 0,
                                    '& .MuiStepLabel-iconContainer': {
                                      pr: 0,
                                      position: 'relative',
                                      zIndex: 1,
                                      bgcolor: Colors.white,
                                      borderRadius: '50%'
                                    },
                                    '& .MuiStepLabel-labelContainer': {
                                      ml: 1.5,
                                      minWidth: 0
                                    }
                                  }
                                : {}
                            }
                          >
                            {!isMobile && dataResposta && (
                              <Typography variant="caption" sx={{ display: 'block' }}>
                                {new Date(dataResposta).toLocaleString()}
                              </Typography>
                            )}
                            <Typography
                              sx={{
                                fontSize: isMobile ? '0.875rem' : 'inherit',
                                fontWeight: isMobile ? 600 : 'inherit'
                              }}
                            >
                              {nomeFase}
                            </Typography>
                            {isMobile && (
                              <>
                                <Typography
                                  variant="caption"
                                  display="block"
                                  color={stepStatus.color}
                                >
                                  {stepStatus.label}
                                </Typography>
                                {dataResposta && (
                                  <Typography
                                    variant="caption"
                                    display="block"
                                    color={Colors.gray1}
                                  >
                                    {new Date(dataResposta).toLocaleString()}
                                  </Typography>
                                )}
                              </>
                            )}
                          </StepLabel>
                        </Step>
                      );
                    })}
                </Stepper>
              </Collapse>
            </Box>
          </Box>
        )}

        <ExibirConsumoPosPago />
      </>
    );
  }

  return (
    <Grid item xs={12}>
      {posPagoNaoSolicitado ? (
        <Box
          data-tour-pj="postpaid"
          p={isMobile ? 2 : 5}
          display="flex"
          borderRadius={2}
          flexDirection="column"
          boxShadow=" 0px 5px 20px rgba(0, 0, 0, 0.15)"
        >
          <Typography variant="h6" color={Colors.gray1}>
            Torne-se um cliente pós pago.
          </Typography>

          <Typography color={Colors.gray1} my={3} textAlign="justify">
            Ao tornar-se um cliente PJ pós pago, você poderá efetuar recargas que serão faturadas no
            mês subsequente ao mês do consumo.
          </Typography>

          {showModalAnexoMinutaCondition && <ModalAnexoMinuta refreshUserInfo={refreshUserInfo} />}
        </Box>
      ) : (
        <>
          {fasesContrato.length > 0 && (
            <Box
              data-tour-pj="postpaid"
              p={isMobile ? 2 : 5}
              display="flex"
              borderRadius={2}
              flexDirection="column"
              boxShadow=" 0px 5px 20px rgba(0, 0, 0, 0.15)"
            >
              <Typography variant="h6" color={Colors.gray1}>
                {title}
              </Typography>

              <Typography color={Colors.gray1} my={3} textAlign="justify">
                {description}
              </Typography>

              <Typography variant="subtitle2" color={Colors.gray1} mb={1}>
                Fluxo de Contrato
              </Typography>

              <Stepper
                activeStep={activeStepContrato}
                orientation={isMobile ? 'vertical' : 'horizontal'}
                sx={{ mt: 1 }}
                connector={isMobile ? null : <QontoConnector isRejected={posPagoRejeitado} />}
                alternativeLabel={!isMobile}
              >
                {[...fasesContrato]
                  .sort((a, b) => a.ordemFase - b.ordemFase)
                  .map((fase, index) => {
                    const { nomeFase, dataResposta, ordemFase, faseAprovada } = fase;
                    const isCompleted = dataResposta !== null;
                    const stepStatus = getStepStatus(
                      fase,
                      isCompleted,
                      index === activeStepContrato
                    );
                    return (
                      <Step
                        completed={isCompleted}
                        key={`c-${ordemFase}`}
                        sx={getMobileStepSx(isCompleted && faseAprovada !== false)}
                      >
                        <StepLabel
                          error={dataResposta !== null && faseAprovada === false}
                          StepIconComponent={QontoStepIcon}
                          sx={
                            isMobile
                              ? {
                                  alignItems: 'flex-start',
                                  py: 0,
                                  '& .MuiStepLabel-iconContainer': {
                                    pr: 0,
                                    position: 'relative',
                                    zIndex: 1,
                                    bgcolor: Colors.white,
                                    borderRadius: '50%'
                                  },
                                  '& .MuiStepLabel-labelContainer': {
                                    ml: 1.5,
                                    minWidth: 0
                                  }
                                }
                              : {}
                          }
                        >
                          {!isMobile && dataResposta && (
                            <Typography variant="caption" sx={{ display: 'block' }}>
                              {new Date(dataResposta).toLocaleString()}
                            </Typography>
                          )}
                          <Typography
                            sx={{
                              fontSize: isMobile ? '0.875rem' : 'inherit',
                              fontWeight: isMobile ? 600 : 'inherit'
                            }}
                          >
                            {nomeFase}
                          </Typography>
                          {isMobile && (
                            <>
                              <Typography
                                variant="caption"
                                display="block"
                                color={stepStatus.color}
                              >
                                {stepStatus.label}
                              </Typography>
                              {dataResposta && (
                                <Typography variant="caption" display="block" color={Colors.gray1}>
                                  {new Date(dataResposta).toLocaleString()}
                                </Typography>
                              )}
                            </>
                          )}
                        </StepLabel>
                      </Step>
                    );
                  })}
              </Stepper>
            </Box>
          )}
        </>
      )}
    </Grid>
  );
};

export default ConsumoPosPago;
